import { toMl } from './units.js';
import type { RecipeLine, Settings, Technique, Unit } from './types.js';

/**
 * Rough volumes for non-measured units, in ml. These are conventions, not measurements:
 * a bar dash is ~0.6 ml, a barspoon ~5 ml. 'piece' (an egg white, a cherry) is ignored.
 */
export const UNIT_ML: Partial<Record<Unit, number>> = { dash: 0.6, drop: 0.05, barspoon: 5 };

export interface Estimate {
  /** Alcohol by volume of the finished drink, percent. */
  abvPct: number;
  /** Finished volume including dilution, ml. */
  volumeMl: number;
  /** Ingredient volume before dilution, ml. */
  ingredientMl: number;
  /** True if any line had no usable volume (e.g. 'piece') and was left out. */
  ignoredLines: number;
}

type DilutionSettings = Pick<
  Settings,
  'dilutionShake' | 'dilutionStir' | 'dilutionBuild' | 'dilutionBlend'
>;

export function dilutionFor(technique: Technique, s: DilutionSettings): number {
  switch (technique) {
    case 'shake':
      return s.dilutionShake;
    case 'stir':
      return s.dilutionStir;
    case 'build':
      return s.dilutionBuild;
    case 'blend':
      return s.dilutionBlend;
  }
}

/** Volume of one line in ml, or null if it can't be estimated. */
export function lineMl(line: Pick<RecipeLine, 'amount' | 'unit'>): number | null {
  const direct = toMl(line.amount, line.unit);
  if (direct !== null) return direct;
  const per = UNIT_ML[line.unit];
  return per === undefined ? null : per * line.amount;
}

/**
 * Estimate only (not lab accurate): ABV = alcohol / (ingredient volume x (1 + dilution)).
 * Ignores volume contraction when alcohol and water mix. Returns null with nothing measurable.
 */
export function estimateDrink(
  lines: Pick<RecipeLine, 'amount' | 'unit' | 'abvPct'>[],
  technique: Technique,
  settings: DilutionSettings,
): Estimate | null {
  let ingredientMl = 0;
  let alcoholMl = 0;
  let ignoredLines = 0;
  for (const l of lines) {
    const ml = lineMl(l);
    if (ml === null) {
      ignoredLines++;
      continue;
    }
    ingredientMl += ml;
    alcoholMl += (ml * l.abvPct) / 100;
  }
  if (ingredientMl <= 0) return null;
  const volumeMl = ingredientMl * (1 + dilutionFor(technique, settings));
  return { abvPct: (alcoholMl / volumeMl) * 100, volumeMl, ingredientMl, ignoredLines };
}
